import { ReactNode } from "react";

import { ContactBookOption } from "@RequestFormV2/sections/Schedule/utils/contactBook";

type SelectOptionMeta = { context: string; inputValue: string };

type PortOptionLabelInput = {
  label: string;
  city_name?: string;
  country_name?: string;
  code?: string;
  un_locode?: string;
  portType?: "airport" | "seaport";
};

function getPortLocationCode(option: PortOptionLabelInput): string {
  if (option.portType === "seaport") {
    return option.un_locode ?? "";
  }

  return option.code ?? "";
}

function getPortLocationDisplayText(option: PortOptionLabelInput): string {
  const locationCode = getPortLocationCode(option);
  const codePart = locationCode ? `(${locationCode}) ` : "";
  const countryPart = option.country_name ?? "";
  const labelPart = option.label || option.city_name || "";

  if (countryPart && labelPart) {
    return `${codePart}${countryPart} - ${labelPart}`;
  }

  if (countryPart) {
    return `${codePart}${countryPart}`.trim();
  }

  return `${codePart}${labelPart}`.trim();
}

type AddressOptionInput = {
  label: string;
  street_address?: string;
  street_address_line_two?: string;
  city_name?: string;
  state_name?: string;
  zipcode?: string;
  country_name?: string;
  un_locode?: string;
  location_reference_number?: string;
  custom_address_reference?: string;
};

export function highlightSearchText(text: string, search: string): ReactNode {
  if (!text) return text;
  if (!search?.trim()) return text;

  const lowerText = text.toLowerCase();
  const lowerSearch = search.toLowerCase();
  const nodes: ReactNode[] = [];
  let startIndex = 0;
  let matchIndex = lowerText.indexOf(lowerSearch, startIndex);

  while (matchIndex !== -1) {
    if (matchIndex > startIndex) {
      nodes.push(text.slice(startIndex, matchIndex));
    }
    nodes.push(
      <span key={matchIndex} className="rfv2-select-option-label__match">
        {text.slice(matchIndex, matchIndex + search.length)}
      </span>,
    );
    startIndex = matchIndex + search.length;
    matchIndex = lowerText.indexOf(lowerSearch, startIndex);
  }

  if (startIndex < text.length) {
    nodes.push(text.slice(startIndex));
  }

  if (nodes.length === 0) return text;
  if (nodes.length === 1) return nodes[0];

  return <>{nodes}</>;
}

export function formatPortOptionLabel(option: PortOptionLabelInput, meta: SelectOptionMeta) {
  const displayText = getPortLocationDisplayText(option);

  if (meta.context === "value") {
    return displayText;
  }

  return (
    <div className="p-1">
      <div className="rfv2-select-option-label">{highlightSearchText(displayText, meta.inputValue)}</div>
    </div>
  );
}

export function formatContactOptionLabel(option: ContactBookOption, meta: SelectOptionMeta) {
  if (meta.context === "value") {
    return option.label;
  }

  return (
    <div className="p-1">
      <div className="rfv2-select-option-label">{highlightSearchText(option.label, meta.inputValue)}</div>
      {option.company_name && (
        <div className="rfv2-select-option-label rfv2-select-option-label--secondary mt-1">{option.company_name}</div>
      )}
    </div>
  );
}

export function formatAddressOptionLabel(option: AddressOptionInput, meta: SelectOptionMeta) {
  if (meta.context === "value") {
    return option.label;
  }

  const search = meta.inputValue;

  return (
    <div className="p-1">
      <div className="rfv2-select-option-label">{highlightSearchText(option.label, search)}</div>
      <div className="rfv2-select-option-label rfv2-select-option-label--secondary mt-xs">
        {option.street_address && (
          <>
            {highlightSearchText(option.street_address, search)}
            {option.street_address_line_two && (
              <>
                {", "}
                {highlightSearchText(option.street_address_line_two, search)}
              </>
            )}
          </>
        )}
        {(option.city_name || option.state_name || option.zipcode) && (
          <>
            <br />
            {option.city_name && highlightSearchText(option.city_name, search)}
            {option.state_name && (
              <>
                {", "}
                {highlightSearchText(option.state_name, search)}
              </>
            )}
            {option.zipcode && <> {highlightSearchText(option.zipcode, search)}</>}
          </>
        )}
        {option.country_name && (
          <>
            <br />
            {highlightSearchText(option.country_name, search)}
          </>
        )}
      </div>
      {option.un_locode && (
        <div className="rfv2-select-option-label rfv2-select-option-label--secondary mt-xs">
          UN/LOCODE: {option.un_locode}
        </div>
      )}
      {(option.location_reference_number || option.custom_address_reference) && (
        <div className="rfv2-select-option-label rfv2-select-option-label--secondary mt-xs">
          {option.location_reference_number}
          {option.location_reference_number && option.custom_address_reference && " | "}
          {option.custom_address_reference}
        </div>
      )}
    </div>
  );
}
